import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {IntegrationModule} from '../../../../model/integration-module';
import {Decoder} from '../../../../model/decoder';
import {Tenant} from '../../../../model/tenant';
import {TenantsService} from '../../../../services/tenants.service';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {JsonEditorService} from '../../../../services/json-editor.service';
import {CommonMessagesService} from '../../../../services/common-messages.service';
import {DevicesService} from '../../../../services/devices.service';
import {noSpecialCharacters} from '../../../../shared/custom-validators';
import {PreviousRouteService} from '../../../../services/previous-route.service';
import {Encoder} from '../../../../model/encoder';
import {DefaultDecoder} from '../../../../model/default-decoder';
import {DefaultEncoder} from '../../../../model/default-encoder';
import {LoggedUser} from '../../../../common/logged-user.service';

@Component({
  selector: 'app-devices-form-integration-modules',
  templateUrl: './devices-form-integration-modules.component.html',
  styleUrls: ['./devices-form-integration-modules.component.scss']
})
export class DevicesFormIntegrationModulesComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() integrationModules: IntegrationModule[];
  @Output() integrationModulesChange = new EventEmitter<IntegrationModule[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  integrationModulesForm: FormGroup;
  rowsForm: FormArray;

  decoders: Decoder[];
  encoders: Encoder[];
  integrationCodes: string[] = [];

  constructor(
    private tenantsService: TenantsService,
    private devicesService: DevicesService,
    public jsonEditorService: JsonEditorService,
    private commonMessagesService: CommonMessagesService,
    private formBuilder: FormBuilder,
    private previousService: PreviousRouteService
  ) {
    this.integrationCodes = [
      'IOTSENS',
      'LORA',
      'WMBUS',
      'UNE',
      'CONTAZARA',
      'SHERLOG'
    ];
  }

  ngOnInit(): void {
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    const promises = [
      this.tenantsService.getTenantDecoders(this.tenant.uuid).toPromise(),
      this.tenantsService.getTenantEncoders(this.tenant.uuid).toPromise()
    ];
    Promise.all(promises).then((values: any[]) => {
      this.decoders = values[0].decoders.sort((a, b) => a.name > b.name ? 1 : -1);
      this.encoders = values[1].encoders.sort((a, b) => a.name > b.name ? 1 : -1);
      this.createForm(this.integrationModules);
    });
  }

  private createForm(integrationModules: IntegrationModule[]): void {
    this.rowsForm = this.formBuilder.array(
      integrationModules.map(module => this.formGroupFromIntegrationModule(module))
    );
    this.integrationModulesForm = this.formBuilder.group({
      integrationModules: this.rowsForm
    });
  }

  addModule(): void {
    const newModule = this.formGroupFromIntegrationModule(new IntegrationModule());
    this.rowsForm.push(newModule);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  isDefaultDecoder(rowIndex: number): boolean {
    return this.rowsForm.value[rowIndex].decoder instanceof DefaultDecoder;
  }

  isDefaultEncoder(rowIndex: number): boolean {
    return this.rowsForm.value[rowIndex].encoder instanceof DefaultEncoder;
  }

  getDecoders(rowIndex: number): (Decoder | DefaultDecoder)[] {
    if (this.isDefaultDecoder(rowIndex)) {
      return [this.rowsForm.value[rowIndex].decoder];
    } else {
      return this.decoders;
    }
  }

  getEncoders(rowIndex: number): (Encoder | DefaultEncoder)[] {
    if (this.isDefaultEncoder(rowIndex)) {
      return [this.rowsForm.value[rowIndex].encoder];
    } else {
      return this.encoders;
    }
  }

  onPreviousStep(): void {
    const modules = this.integrationModulesForm.get('integrationModules').value;
    this.previousStep.emit();
    this.integrationModulesChange.emit(modules);
  }

  onNextStep(): void {
    if (this.integrationModulesForm.valid) {
      const modules = this.integrationModulesForm.get('integrationModules').value;
      this.nextStep.emit();
      this.integrationModulesChange.emit(modules);
    }
  }

  private formGroupFromIntegrationModule(module: IntegrationModule): FormGroup {
    return this.formBuilder.group({
      integrationIdentifier: [module.integrationIdentifier, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      integrationCode: [module.integrationCode, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      technicalDetails: [module.technicalDetails],
      additionalInfo: [module.additionalInfo],
      decoder: [module.decoder],
      encoder: [module.encoder]
    });
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  confirmDelete(rowIndex: number): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.onRowDelete(rowIndex);
    });
  }

}
